import { SetStateAction, Dispatch } from 'react';
import { useRouter } from 'next/navigation';
import { TradeOfferOperation, AmountFor } from '@bts-web/data-layer/server';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import { getAssetTradeUrl } from '../../../../../../route-builders';
import {
  isDevelopmentEnv,
  setWindowHistoryState,
} from '@bts-web/utils-functions';
import { IUseTradeForm } from '../useTradeForm';
import { TradeInputErrorType } from '../../useTradeInput';
import { initiateTrade } from '../../../../../../common/gqlActions/initiateTrade.action';
import { setInputValueToLocalStorage } from '../../../../utils/localStorageInputValueUtils';
import {
  datadogErrorHelper,
  SegmentLimitsModal,
  valueFromAssetToFiat,
} from '../../../../../../common';
import { processDeepLink } from '../../../../utils/processDeepLink';
import { extractOfferIdFromAcceptUrl } from '../../../../utils/extractOfferIdFromAcceptUrl';
import { ShowModalFunction } from '../../../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { getAppConfig } from '@bts-web/core-app-config';

export const createTradeOffer = async (
  formData: FormData,
  data: {
    currentTradeDetails: IUseTradeForm['currentTradeDetails'];
    assetDetails: IUseTradeForm['assetDetails'];
    transactionType: TradeOfferOperation;
  },
  handlers: {
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    setErrors: Dispatch<SetStateAction<TradeInputErrorType>>;
    processErrors: (gqlResponseErrors?: string[]) => Promise<void>;
    router: ReturnType<typeof useRouter>;
    showModal?: ShowModalFunction;
  },
  flags: {
    feature_showSegmentLimitsError?: boolean;
    feature_enableFiatTransfers?: boolean;
  } = {},
) => {
  let formattedAmount = (formData.get('amount') as string).replace(/,/g, '.');

  let amountFor: AmountFor = formData.get('amountFor') as AmountFor;

  const { appId } = getAppConfig();

  const { feature_showSegmentLimitsError, feature_enableFiatTransfers } = flags;

  // Handle local storage
  if (amountFor === 'FIAT') {
    setInputValueToLocalStorage({
      value: formattedAmount,
      assetId: data.assetDetails.id,
    });
  } else {
    const fiatValue =
      valueFromAssetToFiat(formattedAmount, data.assetDetails.price)?.value ??
      '';

    setInputValueToLocalStorage({
      value: fiatValue,
      assetId: data.assetDetails.id,
    });
  }

  // Handle sell maximum exception
  if (
    data.transactionType === 'SELL' &&
    amountFor === 'FIAT' &&
    Number(formattedAmount) ===
      Number(data?.currentTradeDetails.maximumTradeAmount?.FIAT?.value)
  ) {
    amountFor = 'ASSET';

    formattedAmount = data?.currentTradeDetails.maximumTradeAmount?.ASSET
      ?.value as string;
  }

  // Handle sell minimum exception
  if (
    data.transactionType === 'SELL' &&
    amountFor === 'ASSET' &&
    data.currentTradeDetails.minimumTradeAmount.ASSET?.value &&
    data.currentTradeDetails.minimumTradeAmount.FIAT?.value &&
    Number(formattedAmount) ===
      Number(data.currentTradeDetails.minimumTradeAmount.ASSET.value)
  ) {
    amountFor = 'FIAT';

    formattedAmount = data.currentTradeDetails.minimumTradeAmount.FIAT.value;
  }

  try {
    const resp = await initiateTrade({
      amount: formattedAmount,
      amountFor,
      assetId: data.assetDetails.id,
      operation: data.transactionType,
    });

    if (resp.errors) {
      if (
        feature_showSegmentLimitsError &&
        resp.data?.createTradeOffer?.__typename === 'CreateTradeOfferError' &&
        handlers.showModal &&
        resp.data.createTradeOffer.code
      ) {
        handlers.showModal(SegmentLimitsModal, {
          type: resp.data.createTradeOffer.code,
          details: resp.data.createTradeOffer.details,
          assetId: data.assetDetails.id,
        });
      } else {
        handlers.processErrors(resp.errors);
      }

      handlers.setIsLoading(false);

      return;
    }

    const { createTradeOffer } = resp?.data ?? {};

    const offerId =
      (createTradeOffer as { offer?: { id: string }; id?: string })?.offer
        ?.id ?? (createTradeOffer as { id?: string })?.id;

    const deepLink = createTradeOffer?.deepLink;

    const processedDeepLink = processDeepLink(deepLink);

    const resolvedOfferId =
      offerId ?? extractOfferIdFromAcceptUrl(processedDeepLink ?? undefined);

    if (!createTradeOffer) {
      reportErrorAndRedirectToGeneric('Missing offer data', handlers);

      return;
    }

    if (processedDeepLink) {
      const skipRlbSell2FA = appId === 1 && data.transactionType === 'SELL';

      const skipRlbTirolSell2FA =
        appId === 6 && data.transactionType === 'SELL';

      const skipRlbStmkSell2FA = appId === 9 && data.transactionType === 'SELL';

      const skipRlbSalzburgSell2FA =
        appId === 10 && data.transactionType === 'SELL';

      const skipRlbVorarlbergSell2FA =
        appId === 11 && data.transactionType === 'SELL';

      const skipBforSellandBuy2FA = appId === 7;

      const skipBwBank2FA = appId === 8;

      const skipIcard2FA = appId === 12;

      const skipSgForge2FA = appId === 4;

      const skipLbbw2FA = appId === 2;

      const skipMoneliq2FA = appId === 13;

      const skipTradenOoeSell2FA =
        appId === 14 && data.transactionType === 'SELL';

      if (
        skipSgForge2FA ||
        skipLbbw2FA ||
        skipRlbSell2FA ||
        skipRlbTirolSell2FA ||
        skipRlbStmkSell2FA ||
        skipRlbSalzburgSell2FA ||
        skipRlbVorarlbergSell2FA ||
        skipBforSellandBuy2FA ||
        skipBwBank2FA ||
        skipIcard2FA ||
        skipMoneliq2FA ||
        skipTradenOoeSell2FA
      ) {
        if (!resolvedOfferId || !data.assetDetails.id) {
          reportErrorAndRedirectToGeneric('Missing offer data', handlers);

          return;
        }

        const variant = data.transactionType === 'BUY' ? 'buy' : 'sell';

        const acceptUrl = getAssetTradeUrl(data.assetDetails.id, {
          type: 'acceptOffer',
          variant,
          offerId: resolvedOfferId,
        });

        handlers.router.push(acceptUrl);

        return;
      }

      if (typeof window !== 'undefined') {
        setWindowHistoryState({
          newHistoryState: {
            key: 'verifyLink',
            value: processedDeepLink,
          },
          searchParams: window.location.search,
          pathname: window.location.pathname || '/',
        });
      }

      handlers.setIsLoading(false);

      return;
    }

    const transferLink = createTradeOffer?.paymentLink ?? undefined;

    if (transferLink && feature_enableFiatTransfers) {
      handlers.router.push(
        getAssetTradeUrl(data.assetDetails.id, {
          type: 'fiatTransfer',
          variant: 'buy',
          offerId: offerId as string,
          transferLink,
        }),
      );

      return;
    }

    if (!resolvedOfferId) {
      reportErrorAndRedirectToGeneric('Missing offer data', handlers);

      return;
    }

    const route = data.transactionType === 'BUY' ? 'buy' : 'sell';

    handlers.router.push(
      getAssetTradeUrl(data.assetDetails.id, {
        type: 'acceptOffer',
        variant: route,
        offerId: resolvedOfferId,
      }),
    );
  } catch (err) {
    const stringifiedError = JSON.stringify(err);

    handlers.setIsLoading(false);

    handlers.setErrors((state) => ({
      ...state,
      customError: stringifiedError,
    }));

    reportErrorAndRedirectToGeneric(stringifiedError, handlers);
  }
};

function reportErrorAndRedirectToGeneric(
  errorMessage: string,
  handlers: { router: ReturnType<typeof useRouter> },
): void {
  if (!isDevelopmentEnv()) {
    datadogErrorHelper({
      errorMessage,
      errorSeverity: DatadogErrorLevels.CRITICAL,
    });

    handlers.router.push(`/error/generic`);
  } else {
    console.error(errorMessage);
  }
}
